import { useEffect, useState } from "react";
import { api, buildWhatsAppLink } from "@/lib/api";
import { LOGO_DARK } from "@/lib/brand";
import { useReveal } from "@/hooks/use-reveal";
import {
  Waves, Trees, Sofa, Armchair, Flame, Music, Car, ChefHat,
  Sparkles, Wine, Droplets, Projector, MapPin, Phone, Instagram, Facebook, Youtube,
  ArrowUpRight, Check, Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import BookingDialog from "@/components/public/BookingDialog";
import PublicNav from "@/components/public/PublicNav";
import { PublicSiteSkeleton } from "@/components/public/Skeletons";

const ICON_MAP = { Waves, Trees, Sofa, Armchair, Flame, Music, Car, ChefHat, Sparkles, Wine, Droplets, Projector };

function Eyebrow({ children }) {
  return (
    <span className="text-xs sm:text-sm font-medium uppercase tracking-[0.22em] text-[#FF6B35]">
      {children}
    </span>
  );
}

function ScrollProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const total = h.scrollHeight - h.clientHeight;
      setPct(total > 0 ? (h.scrollTop / total) * 100 : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div className="fixed top-0 left-0 right-0 z-[60] h-[2px] bg-transparent pointer-events-none">
      <div
        className="h-full bg-[#FF6B35] origin-left"
        style={{ width: `${pct}%`, transition: "width 120ms linear" }}
      />
    </div>
  );
}

function SectionHeading({ eyebrow, title, sub, center }) {
  return (
    <div className={`reveal flex flex-col gap-4 ${center ? "items-center text-center" : ""}`}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-earth-900 leading-[1.05]">
        {title}
      </h2>
      {sub && (
        <p className={`text-earth-700 font-light text-base sm:text-lg leading-relaxed max-w-2xl ${center ? "mx-auto" : ""}`}>
          {sub}
        </p>
      )}
    </div>
  );
}


function Hero({ hero, settings, onBook }) {
  const wa = buildWhatsAppLink(settings?.whatsapp_number);
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    const on = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  const parallax = `translateY(${scrollY * 0.25}px) scale(${1 + scrollY * 0.0002})`;
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[82vh] flex items-end pb-10 sm:pb-14 overflow-hidden rounded-b-[2rem]"
    >
      <img
        src={hero?.background_image}
        alt="Aiyavi Retreat"
        className="absolute inset-0 w-full h-full object-cover will-change-transform animate-ken-burns"
        style={{ transform: parallax }}
      />
      <div className="absolute inset-0 hero-overlay" />

      {/* Floating decorative orbs */}
      <div className="absolute top-[35%] right-[12%] w-2 h-2 rounded-full bg-[#FF6B35]/70 animate-drift" />
      <div className="absolute top-[55%] right-[20%] w-1.5 h-1.5 rounded-full bg-white/50 animate-drift" style={{ animationDelay: "1.2s" }} />
      <div className="absolute top-[30%] right-[28%] w-1 h-1 rounded-full bg-white/40 animate-drift" style={{ animationDelay: "2.4s" }} />

      <div className="relative z-10 max-w-7xl mx-auto w-full px-6">
        <div className="flex flex-col gap-4 max-w-3xl text-white">
          <span className="reveal inline-flex items-center gap-2 text-xs sm:text-sm uppercase tracking-[0.22em] text-white/85">
            <span className="w-8 h-px bg-[#FF6B35]" />
            {hero?.eyebrow || "Private Farmhouse · Hyderabad"}
          </span>
          <h1
            className="reveal-blur font-serif text-4xl sm:text-6xl lg:text-7xl font-light leading-[1.05] tracking-tight drop-shadow-[0_4px_16px_rgba(0,0,0,0.55)]"
            data-testid="hero-heading"
          >
            {hero?.heading}
          </h1>
          <p className="reveal text-white/90 font-light text-base sm:text-lg max-w-xl leading-relaxed drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]">
            {hero?.subheading}
          </p>
          <div className="reveal flex flex-wrap gap-3 pt-2">
            <a
              href={wa}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp-btn"
              className="group inline-flex items-center gap-2 bg-[#FF6B35] text-white rounded-full px-7 py-3.5 font-medium hover:bg-[#ff7d4d] transition-all duration-300 shadow-[0_12px_30px_-10px_rgba(255,107,53,0.6)] hover:shadow-[0_20px_50px_-12px_rgba(255,107,53,0.8)] hover:-translate-y-0.5"
            >
              {hero?.cta_text || "Book Your Stay"}
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
            </a>
            <button
              onClick={onBook}
              data-testid="hero-enquire-btn"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/40 text-white rounded-full px-7 py-3.5 font-medium hover:bg-white/20 transition-colors"
            >
              Send an enquiry
            </button>
          </div>
        </div>
      </div>

      {/* Scroll-down hint */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2 text-white/70 text-[10px] uppercase tracking-[0.3em] pointer-events-none">
        <span>Scroll</span>
        <span className="w-px h-8 bg-white/40 animate-scroll-hint" />
      </div>
    </section>
  );
}

function About({ about }) {
  return (
    <section id="about" data-testid="about-section" className="py-12 sm:py-16 px-6">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-6 lg:gap-12 items-center">
        <div className="lg:col-span-5 flex flex-col gap-3">
          <Eyebrow>About Aiyavi</Eyebrow>
          <h2 className="reveal font-serif text-4xl sm:text-5xl font-light tracking-tight text-earth-900 leading-[1.05]">
            {about?.title}
          </h2>
          <p className="reveal text-earth-700 font-light text-base sm:text-lg leading-relaxed whitespace-pre-line">
            {about?.body}
          </p>
        </div>
        <div className="lg:col-span-7 reveal">
          <div className="relative aspect-[4/3] rounded-[2rem] overflow-hidden shadow-[0_20px_60px_-20px_rgba(92,64,51,0.35)]">
            {about?.image && (
              <img src={about.image} alt="About Aiyavi" className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms] ease-out" />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Location({ location }) {
  return (
    <section id="location" data-testid="location-section" className="py-12 sm:py-16 px-6 bg-beige/40">
      <div className="max-w-7xl mx-auto">
        <SectionHeading eyebrow="Location" title="Just beyond Hyderabad's edge." sub={location?.address} />
        <div className="grid md:grid-cols-12 gap-5 mt-6">
          <div className="md:col-span-7 reveal rounded-[2rem] overflow-hidden aspect-[16/10] border border-earth-100">
            {location?.map_embed_url && (
              <iframe
                title="map"
                src={location.map_embed_url}
                className="w-full h-full"
                loading="lazy"
              />
            )}
          </div>
          <div className="md:col-span-5 flex flex-col gap-3 stagger">
            {(location?.highlights || []).map((h) => (
              <div
                key={h.id}
                className="reveal bg-white rounded-2xl p-6 border border-earth-100 hover:border-sage-300 transition-colors"
                data-testid={`highlight-${h.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-1 w-8 h-8 rounded-full bg-[#FF6B35]/12 ring-1 ring-[#FF6B35]/30 flex items-center justify-center">
                    <Check className="w-4 h-4 text-[#FF6B35]" strokeWidth={2} />
                  </div>
                  <div>
                    <div className="font-serif text-xl text-earth-900">{h.title}</div>
                    <div className="text-sm text-earth-600 mt-1">{h.description}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function AmenityDialog({ open, onOpenChange, amenity }) {
  if (!amenity) return null;
  const Icon = ICON_MAP[amenity.icon] || Sparkles;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="amenity-dialog"
        className="sm:max-w-3xl bg-white rounded-3xl border-earth-100 p-0 overflow-hidden"
      >
        <div className="relative aspect-[16/9] bg-earth-100 overflow-hidden">
          {amenity.image_url ? (
            <img src={amenity.image_url} alt={amenity.name} className="absolute inset-0 w-full h-full object-cover animate-ken-burns" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#FFF6F0] to-[#FFE4D2]">
              <Icon className="w-20 h-20 text-[#FF6B35]" strokeWidth={1.2} />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-earth-900/60 via-transparent to-transparent pointer-events-none" />
          <div className="absolute bottom-5 left-5 sm:bottom-6 sm:left-6 flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FF6B35] to-[#FF8A5C] flex items-center justify-center shadow-[0_12px_28px_-10px_rgba(255,107,53,0.7)]">
              <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <div className="text-white">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#FFB890]">Amenity</div>
              <DialogTitle className="font-serif text-3xl font-light leading-tight" data-testid="amenity-dialog-title">
                {amenity.name}
              </DialogTitle>
            </div>
          </div>
        </div>
        <div className="p-6 sm:p-8">
          <DialogDescription className="text-base text-earth-700 font-light leading-relaxed">
            {amenity.description || "Included with every stay at Aiyavi Retreat."}
          </DialogDescription>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Amenities({ amenities }) {
  const visible = amenities.filter((a) => a.enabled !== false);
  const [active, setActive] = useState(null);
  return (
    <section id="amenities" data-testid="amenities-section" className="py-12 sm:py-16 px-6 bg-gradient-to-b from-earth-50 via-beige/20 to-earth-50">
      <div className="max-w-7xl mx-auto">
        <div className="reveal flex flex-col gap-3">
          <span className="text-xs sm:text-sm font-medium uppercase tracking-[0.22em] text-[#FF6B35]">Amenities</span>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-earth-900 leading-[1.05]">
            Everything you need. <span className="italic text-earth-500">Nothing you don't.</span>
          </h2>
          <p className="text-earth-600 text-sm sm:text-base">Tap any card to see it up close.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-6 stagger">
          {visible.map((a, i) => {
            const Icon = ICON_MAP[a.icon] || Sparkles;
            const num = String(i + 1).padStart(2, "0");
            return (
              <button
                type="button"
                key={a.id}
                onClick={() => setActive(a)}
                className="reveal tilt-card gradient-border group relative overflow-hidden rounded-3xl p-6 bg-gradient-to-br from-white via-[#FFF6F0] to-[#FFE4D2] border border-[#FF6B35]/30 hover:shadow-[0_30px_60px_-25px_rgba(255,107,53,0.45)] text-left"
                data-testid={`amenity-${a.id}`}
              >
                <div className="absolute -top-12 -right-12 w-36 h-36 rounded-full blur-3xl opacity-0 group-hover:opacity-60 transition-opacity duration-700 bg-[#FF6B35]/30" />
                <div className="absolute top-5 right-5 font-serif italic text-xs text-earth-400 group-hover:text-earth-600 transition-colors">
                  /{num}
                </div>
                <div className="relative">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:-rotate-3 bg-gradient-to-br from-[#FF6B35] to-[#FF8A5C] shadow-[0_12px_28px_-10px_rgba(255,107,53,0.6)]">
                    <Icon className="w-7 h-7 text-white" strokeWidth={1.5} />
                  </div>
                </div>
                <div className="mt-5 font-serif text-xl sm:text-2xl text-earth-900 leading-tight">
                  {a.name}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="h-px transition-all duration-500 bg-[#FF6B35] w-8 group-hover:w-14" />
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#FF6B35] font-semibold">View</span>
                </div>
                <ArrowUpRight
                  className="absolute bottom-5 right-5 w-5 h-5 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500 text-[#FF6B35]"
                  strokeWidth={1.5}
                />
              </button>
            );
          })}
        </div>
      </div>
      <AmenityDialog open={!!active} onOpenChange={(v) => !v && setActive(null)} amenity={active} />
    </section>
  );
}

function Testimonials({ items }) {
  const visible = (items || []).filter((t) => t.enabled !== false);
  if (visible.length === 0) return null;
  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-12 sm:py-16 px-6 bg-gradient-to-b from-earth-50 via-beige/30 to-earth-50">
      <div className="max-w-7xl mx-auto">
        <div className="reveal flex flex-col gap-3 max-w-3xl">
          <span className="text-xs sm:text-sm font-medium uppercase tracking-[0.22em] text-[#FF6B35]">Guests · Stories</span>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-earth-900 leading-[1.05]">
            Quiet weekends. <span className="italic text-earth-500">Warm memories.</span>
          </h2>
          <p className="text-earth-600 text-sm sm:text-base">A few words from the families and friends who've stayed with us.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8 stagger">
          {visible.map((t, i) => (
            <figure
              key={t.id}
              className="reveal tilt-card relative rounded-3xl bg-white border border-earth-100 p-7 shadow-[0_18px_40px_-25px_rgba(60,40,20,0.25)] hover:shadow-[0_30px_60px_-25px_rgba(255,107,53,0.35)] transition-shadow duration-500"
              data-testid={`testimonial-${t.id}`}
            >
              <div className="absolute -top-4 left-7 font-serif italic text-7xl leading-none text-[#FF6B35]/30 select-none">"</div>
              <div className="flex gap-1 mb-4">
                {Array.from({ length: Math.max(1, Math.min(5, t.rating || 5)) }).map((_, idx) => (
                  <Star key={idx} className="w-4 h-4 text-[#FF6B35] fill-[#FF6B35]" strokeWidth={1.5} />
                ))}
              </div>
              <blockquote className="font-serif text-lg sm:text-xl text-earth-800 leading-snug font-light">
                {t.quote}
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-3 pt-5 border-t border-earth-100">
                {t.image_url ? (
                  <img src={t.image_url} alt={t.name} loading="lazy" className="w-11 h-11 rounded-full object-cover border border-earth-100" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF8A5C] text-white flex items-center justify-center font-serif text-lg">
                    {(t.name || "G").charAt(0)}
                  </div>
                )}
                <div className="flex flex-col">
                  <span className="text-earth-900 font-medium text-sm">{t.name}</span>
                  {t.role && <span className="text-earth-500 text-xs">{t.role}</span>}
                </div>
                <span className="ml-auto font-serif italic text-xs text-earth-400">/{String(i + 1).padStart(2, "0")}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function Gallery({ gallery }) {
  const [filter, setFilter] = useState("All");
  const cats = ["All", ...Array.from(new Set(gallery.map((g) => g.category)))];
  const items = filter === "All" ? gallery : gallery.filter((g) => g.category === filter);
  return (
    <section id="gallery" data-testid="gallery-section" className="py-12 sm:py-16 px-6 bg-earth-900 text-white rounded-t-[2rem]">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="reveal flex flex-col gap-3 max-w-2xl">
            <span className="text-xs sm:text-sm font-medium uppercase tracking-[0.22em] text-[#FF6B35]">Gallery</span>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
              Soft mornings. <span className="italic text-white/70">Still waters.</span> Firelit nights.
            </h2>
          </div>
          <div className="reveal flex flex-wrap gap-2">
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 py-1.5 rounded-full text-[11px] uppercase tracking-[0.2em] transition-all border ${
                  filter === c
                    ? "bg-[#FF6B35] border-[#FF6B35] text-white shadow-[0_8px_24px_-8px_rgba(255,107,53,0.6)]"
                    : "bg-white/5 border-white/15 text-white/75 hover:bg-white/10 hover:text-white"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-7 columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance] stagger">
          {items.map((g) => (
            <figure
              key={g.id}
              className="reveal break-inside-avoid mb-4 group relative rounded-[1.5rem] overflow-hidden cursor-pointer"
              data-testid={`gallery-item-${g.id}`}
            >
              <img
                src={g.url}
                alt={g.caption || g.category}
                loading="lazy"
                className="w-full h-auto block object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-earth-900/85 via-earth-900/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <figcaption className="absolute bottom-4 left-5 right-5 text-white opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-px bg-[#FF6B35]" />
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#FF6B35]">{g.category}</span>
                </div>
                {g.caption && <div className="font-serif text-xl mt-1.5 leading-tight">{g.caption}</div>}
              </figcaption>
            </figure>
          ))}
        </div>
        {items.length === 0 && (
          <div className="mt-10 text-center text-white/60 text-sm">No photos in this category yet.</div>
        )}
      </div>
    </section>
  );
}

function Pricing({ pricing, settings }) {
  const wa = buildWhatsAppLink(settings?.whatsapp_number);
  return (
    <section id="pricing" data-testid="pricing-section" className="py-16 sm:py-20 px-6">
      <div className="max-w-7xl mx-auto">
        <SectionHeading eyebrow="Pricing" title="Choose your retreat." sub="Transparent rates. All stays come with full access to the farmhouse and grounds." center />
        <div className="grid md:grid-cols-3 gap-5 mt-8 stagger">
          {pricing.map((p) => (
            <div
              key={p.id}
              data-testid={`pricing-card-${p.id}`}
              className={`reveal tilt-card relative rounded-[1.75rem] p-7 sm:p-8 ${
                p.featured
                  ? "bg-earth-900 text-white animate-glow-pulse border border-earth-800"
                  : "bg-beige border border-earth-200 hover:border-[#FF6B35]/50 hover:shadow-[0_24px_50px_-20px_rgba(92,64,51,0.35)]"
              }`}
            >
              {p.featured && (
                <div className="absolute -top-3 left-10 inline-flex items-center gap-1.5 bg-[#FF6B35] text-white text-xs uppercase tracking-[0.18em] px-3 py-1.5 rounded-full shadow-[0_10px_24px_-10px_rgba(255,107,53,0.6)]">
                  <Star className="w-3 h-3" strokeWidth={2} /> Most loved
                </div>
              )}
              <div className={`text-xs uppercase tracking-[0.22em] ${p.featured ? "text-sage-300" : "text-sage-600"}`}>
                {p.duration}
              </div>
              <div className={`font-serif text-2xl mt-2 ${p.featured ? "text-white" : "text-earth-900"}`}>{p.name}</div>
              <div className={`font-serif text-4xl mt-4 ${p.featured ? "text-white" : "text-earth-900"}`}>{p.price}</div>
              <p className={`mt-4 font-light leading-relaxed text-sm ${p.featured ? "text-white/80" : "text-earth-700"}`}>
                {p.description}
              </p>
              <a
                href={wa}
                target="_blank"
                rel="noreferrer"
                className={`mt-6 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${
                  p.featured
                    ? "bg-white text-earth-900 hover:bg-beige"
                    : "bg-earth-900 text-white hover:bg-earth-800"
                }`}
              >
                Book this <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Experience() {
  const items = [
    { t: "Family getaways", d: "Long lunches, pool afternoons, bedtime stories by the fire.", img: "https://images.unsplash.com/photo-1511895426328-dc8714191300?auto=format&fit=crop&w=1200&q=80" },
    { t: "Celebrations", d: "Birthdays, anniversaries, small weddings — under open skies.", img: "https://images.pexels.com/photos/18450198/pexels-photo-18450198.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
    { t: "Weekend retreats", d: "Hit pause. Come unplug. Leave lighter than you arrived.", img: "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=1200&q=80" },
  ];
  return (
    <section data-testid="experience-section" className="py-12 sm:py-16 px-6 bg-beige/40">
      <div className="max-w-7xl mx-auto">
        <SectionHeading eyebrow="Experiences" title="Made for the moments in between." />
        <div className="grid md:grid-cols-3 gap-5 mt-7 stagger">
          {items.map((it, i) => (
            <div key={i} className="reveal group rounded-[1.75rem] overflow-hidden relative aspect-[4/5] cursor-pointer">
              <img src={it.img} alt={it.t} className="absolute inset-0 w-full h-full object-cover smooth-zoom" />
              <div className="absolute inset-0 bg-gradient-to-t from-earth-900/85 via-earth-900/30 to-transparent transition-opacity duration-500" />
              <div className="absolute inset-x-0 bottom-0 p-6 text-white transition-transform duration-500 group-hover:-translate-y-2">
                <div className="flex items-center gap-2 mb-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500">
                  <span className="w-6 h-px bg-[#FF6B35]" />
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#FF6B35]">Discover</span>
                </div>
                <div className="font-serif text-2xl">{it.t}</div>
                <div className="mt-1 text-white/85 font-light text-sm">{it.d}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BookingCTA({ settings, onBook }) {
  const wa = buildWhatsAppLink(settings?.whatsapp_number);
  return (
    <section id="booking" data-testid="booking-cta" className="py-12 sm:py-16 px-6">
      <div className="max-w-6xl mx-auto rounded-[2rem] bg-earth-900 text-white p-8 sm:p-12 lg:p-14 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-sage-500/20 blur-3xl" />
        <div className="relative reveal flex flex-col gap-4 max-w-2xl">
          <Eyebrow>Plan your escape</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05]">
            Let's hold a weekend in your name.
          </h2>
          <p className="text-white/80 font-light text-base">
            Tell us the dates and what you have in mind — we'll take care of the rest.
          </p>
          <div className="flex flex-wrap gap-3 pt-2">
            <a
              href={wa}
              target="_blank"
              rel="noreferrer"
              data-testid="cta-whatsapp-btn"
              className="inline-flex items-center gap-2 bg-[#FF6B35] text-white rounded-full px-7 py-3.5 font-medium hover:bg-[#ff7d4d] transition-all duration-300 shadow-[0_12px_30px_-10px_rgba(255,107,53,0.6)] hover:shadow-[0_20px_50px_-12px_rgba(255,107,53,0.8)] hover:-translate-y-0.5"
            >
              Book Now <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </a>
            <button
              onClick={onBook}
              data-testid="cta-enquire-btn"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/40 rounded-full px-7 py-3.5 font-medium hover:bg-white/20 transition-colors"
            >
              Send an enquiry
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer({ settings, location }) {
  const wa = buildWhatsAppLink(settings?.whatsapp_number);
  return (
    <footer className="px-6 pb-10 pt-6 bg-beige/30" data-testid="footer">
      <div className="max-w-7xl mx-auto border-t border-earth-200 pt-10 grid md:grid-cols-12 gap-8">
        <div className="md:col-span-5">
          <img src={LOGO_DARK} alt="Aiyavi Retreat" className="h-14 w-auto object-contain" />
          <p className="mt-4 text-earth-700 font-light max-w-md">
            A private farmhouse for families, couples and small gatherings — a breezy drive from Hyderabad.
          </p>
        </div>
        <div className="md:col-span-4 text-earth-800">
          <div className="text-xs uppercase tracking-[0.22em] text-sage-600">Visit</div>
          <div className="mt-3 flex items-start gap-2">
            <MapPin className="w-4 h-4 mt-1" strokeWidth={1.5} />
            <p className="font-light">{location?.address}</p>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Phone className="w-4 h-4" strokeWidth={1.5} />
            <a href={`tel:${settings?.phone}`} className="font-light hover:text-earth-900">{settings?.phone}</a>
          </div>
        </div>
        <div className="md:col-span-3 text-earth-800">
          <div className="text-xs uppercase tracking-[0.22em] text-sage-600">Follow Us On</div>
          <div className="mt-3 flex items-center gap-3">
            {settings?.social?.instagram && <a href={settings.social.instagram} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-earth-300 flex items-center justify-center hover:bg-earth-900 hover:text-white transition-colors"><Instagram className="w-4 h-4" strokeWidth={1.5} /></a>}
            {settings?.social?.facebook && <a href={settings.social.facebook} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-earth-300 flex items-center justify-center hover:bg-earth-900 hover:text-white transition-colors"><Facebook className="w-4 h-4" strokeWidth={1.5} /></a>}
            {settings?.social?.youtube && <a href={settings.social.youtube} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-earth-300 flex items-center justify-center hover:bg-earth-900 hover:text-white transition-colors"><Youtube className="w-4 h-4" strokeWidth={1.5} /></a>}
            <a href={wa} target="_blank" rel="noreferrer" className="rounded-full px-4 py-2 bg-sage-500 text-white text-sm hover:bg-sage-600 transition-colors">WhatsApp</a>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto mt-8 flex flex-wrap items-center justify-between text-xs text-earth-600">
        <div>© {new Date().getFullYear()} Aiyavi Retreat. All rights reserved.</div>
        <a href="/admin/login" className="hover:text-earth-900" data-testid="footer-admin-link">Admin</a>
      </div>
    </footer>
  );
}

export default function PublicSite() {
  const [hero, setHero] = useState(null);
  const [about, setAbout] = useState(null);
  const [location, setLocation] = useState(null);
  const [amenities, setAmenities] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [settings, setSettings] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const revealRef = useReveal(!!hero);

  useEffect(() => {
    (async () => {
      const [h, a, l, am, g, p, t, s] = await Promise.all([
        api.get("/content/hero"), api.get("/content/about"), api.get("/content/location"),
        api.get("/content/amenities"), api.get("/content/gallery"),
        api.get("/content/pricing"), api.get("/content/testimonials"),
        api.get("/content/settings"),
      ]);
      setHero(h.data); setAbout(a.data); setLocation(l.data);
      setAmenities(am.data); setGallery(g.data); setPricing(p.data);
      setTestimonials(t.data); setSettings(s.data);
    })().catch(console.error);
  }, []);

  if (!hero) {
    return <PublicSiteSkeleton />;
  }

  return (
    <div ref={revealRef} data-testid="public-site">
      <ScrollProgress />
      <PublicNav settings={settings} onBook={() => setDialogOpen(true)} />
      <Hero hero={hero} settings={settings} onBook={() => setDialogOpen(true)} />
      <About about={about} />
      <Location location={location} />
      <Amenities amenities={amenities} />
      <Testimonials items={testimonials} />
      <Gallery gallery={gallery} />
      <Pricing pricing={pricing} settings={settings} />
      <Experience />
      <BookingCTA settings={settings} onBook={() => setDialogOpen(true)} />
      <Footer settings={settings} location={location} />
      <BookingDialog open={dialogOpen} onOpenChange={setDialogOpen} settings={settings} />
    </div>
  );
}

